import { gql } from "@apollo/client";
import { faListCheck, faPlus } from "@fortawesome/free-solid-svg-icons";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import HTMLCode from "components/common/HTMLCode";
import Page from "components/common/page/Page";
import ModalPortal from "components/modal/ModalPortal";
import { graph } from "config/axios-context";
import { AppRoutes, URI_PARAMETER_KEYS } from "config/router";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
    DeleteHarvestAgricultureWorkDocument,
    HarvestAgricultureWorkFilterInput,
    HarvestAgricultureWorkSortInput,
    HarvestsDatalistQuery,
    HarvestsDatalistQueryVariables,
    SortEnumType
} from "types/graphql";
import { Roles } from "utilities/app-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import WorkNewItemModel from "./partial/WorkNewItemModel";
import WorkView from "./WorkView";
import { appendFilters, DatalistFilterDefinitions, resolveSearchFilterPropertyNames } from "../../../utilities/datalist/datalist-filter-utilities";
import { uuid } from "config/app-constants";
import { DatalistColumnDefinitions } from "utilities/datalist/datalist-utilities";
import DatalistRowActions from "components/common/datalist/partials/DatalistRowActions";
import ListOfNamesView from "../../../components/common/views/ListOfNamesView";
import { deleteMutation } from "utilities/entities/common/delete-mutation";
import { useParams } from "react-router-dom";

const harvestsListPageQuery = gql`
query harvestsDatalist($take: Int, $skip: Int, $where: HarvestAgricultureWorkFilterInput, $order: [HarvestAgricultureWorkSortInput!]) {
    harvestAgricultureWorks {
      count(where:$where)
      items(order:$order, skip:$skip, take:$take, where:$where) {
        id,
        startDate,
        harvests {
          kgSum
          cartQuantitySum
          items {
            id
            fruit {
                name
            }
          }
        }
        season {
          name,
          id
        }
        workers {
          count
          priceSum
        }
      }
    }
  }
`;

type WorkType = Exclude<Exclude<Exclude<HarvestsDatalistQuery['harvestAgricultureWorks'], null | undefined>['items'], null | undefined>[0], null | undefined>

const HarvestListPage = () => {
    const { [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: targetSeasonId } = useParams<{ [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: string }>();
    const context = useAppContext();
    const [t] = useTranslation();
    const datalistRef = useRef<Datalist<WorkType, HarvestAgricultureWorkFilterInput, HarvestAgricultureWorkSortInput>>(null);

    const datalistConfig: DatalistPropsType<WorkType, HarvestAgricultureWorkFilterInput, HarvestAgricultureWorkSortInput> = {
        itemId(i) {
            return i.id;
        },
        name: 'work-datalist-page',
        onItemClick: {
            onClick(item, event) {
                context.navigate(AppRoutes.AGRICULTURE_WORK_VIEW, { 'id': item.id });
            },
            type: 'doubleClick'
        },
        table: {
            columns: [
                {
                    name: 'startDate',
                    type: 'dateOnly',
                    sort: true,
                    filter: {
                        type: 'dateOnly'
                    },
                    className: 'sticky-col'
                },
                {
                    name: 'type',
                    hide: true,
                    resolve(i) {
                        return i.harvests ? t('agriculture.work.type.harvest') : '-';
                    },
                },
                DatalistColumnDefinitions.workerCountColumnDefinition(context),
                {
                    name: 'workersSum',
                    resolve: i => <>{i.workers?.priceSum}<HTMLCode /><HTMLCode code="euro" /></>
                },
                {
                    name: 'kg',
                    resolve: i => i.harvests?.kgSum + ' KG'
                },
                {
                    name: 'fruit',
                    resolve: i => <ListOfNamesView data={i.harvests?.items?.map(h => h?.fruit?.name)} distinct />
                },
                {
                    name: 'cartQuantity',
                    title: 'fields.cart-quantity.shortName',
                    resolve: i => i.harvests?.cartQuantitySum
                },
                DatalistColumnDefinitions.seasonColumnDefinition(context)
            ],
            render: {
                card: i => {
                    return (
                        <div key={`work-datalist-page-cart-${i.id}`} className="w-full border theme-border px-4 py-2 mb-4 hover:border-sunrise-300">
                            <WorkView model={i as any} view="compact" name="work-view-page" />
                            <div className="mx-auto text-center p-4">
                                <DatalistRowActions
                                    item={i}
                                    itemId={i.id}
                                    mutationPermissionRole={Roles.AGRICULTURE_WORK_WRITE}
                                    viewUrl={AppRoutes.AGRICULTURE_WORK_VIEW}
                                    editUrl={AppRoutes.AGRICULTURE_WORK_HARVEST_EDIT}
                                    delete={deleteHarvest}
                                    view="cart"
                                    name="work-view-page-cart-actions"
                                    onDelete={() => datalistRef.current?.reload()}
                                />
                            </div>
                        </div>
                    )
                },
                actions: {
                    mutationPermissionRole: Roles.AGRICULTURE_WORK_WRITE,
                    viewUrl: AppRoutes.AGRICULTURE_WORK_VIEW,
                    editUrl: AppRoutes.AGRICULTURE_WORK_HARVEST_EDIT,
                    delete: async (item: WorkType) => {
                        await deleteHarvest({ id: item.id });
                    }
                }
            }
        },
        dataSource: {
            request: async (params) => {
                let where = params.filters || {};
                if (params.searchText) {
                    const fields = [
                        ['season'],
                        ['harvests', 'some', 'fruit'],
                        ['workers', 'some', 'person'],
                        ['harvests', 'some', 'harvestFruitGroups', 'some', 'fruitGroup'],
                        ['harvests', 'some', 'harvestFruitCategories', 'some', 'fruitCategory'],
                    ];

                    const searchFilter = resolveSearchFilterPropertyNames(fields, params.searchText);
                    where = appendFilters(where, searchFilter);
                }

                if (targetSeasonId && !isNaN(Number(targetSeasonId))) {
                    where = appendFilters(where, {
                        seasonId: {
                            eq: Number(targetSeasonId)
                        }
                    });
                }

                var response = await graph<HarvestsDatalistQuery, HarvestsDatalistQueryVariables>({
                    query: harvestsListPageQuery,
                    variables: {
                        take: params.take,
                        skip: params.skip,
                        order: params.sort,
                        where: where
                    }
                });

                return response.data?.harvestAgricultureWorks as any;
            },
            sort: [{
                name: 'startDate',
                direction: SortEnumType.Desc,
                id: uuid()
            }],
            filter: targetSeasonId ? undefined : {
                id: uuid(),
                logic: 'and',
                filters: [DatalistFilterDefinitions.seasonFilterDefinition(context)]
            },
        }
    }

    const pageActions = [
        {
            icon: faListCheck,
            hide: !context.userHasRole(Roles.AGRICULTURE_SALES_WRITE) || targetSeasonId ? true : false,
            link: AppRoutes.AGRICULTURE_OVERVIEW_WORK_MULTI_UPDATE,
            title: context.t('pages.agriculture.work.harvest-multi-update'),
            name: "multi-upload"
        },
        {
            icon: faPlus,
            link: () => context.modal.open('new-item'),
            hide: !context.userHasRole(Roles.AGRICULTURE_WORK_WRITE) || targetSeasonId ? true : false,
            title: context.t('button.add'),
            name: "add"
        }
    ]

    const deleteHarvest = async (item: { id: number }) => {
        await deleteMutation('DeleteHarvestAgricultureWorkDocument', item.id);
    }

    return (
        <>
            <Page title="pages.agriculture.work.harvest.list" wFull actions={pageActions}>
                <Datalist ref={datalistRef}
                    {...datalistConfig}
                />
            </Page>
            <ModalPortal title="common.select" name="new-item">
                <WorkNewItemModel />
            </ModalPortal>
        </>
    );
};

export default HarvestListPage;
